// ===========================================
// Missing-price error helpers (invoice generation)
// ===========================================
// Invoice generation throws 422 with `details.missingPrices` when a lesson lacks
// a matrix price at the required duration/category. These helpers pull that
// structured list off the (axios) error and format it for display.

/** One missing pricing-matrix cell — mirrors the backend MissingPrice shape. */
export interface MissingPrice {
  lessonId: string;
  lessonName: string;
  programLevelName: string | null;
  durationMinutes: number;
  category: 'INDIVIDUAL' | 'GROUP' | string;
  /** The cell exists but is $0.00 — invoicing treats that as missing (never bills $0). */
  zeroPrice?: boolean;
  /** The class has no program level — the fix is on the class, not on Class Pricing. */
  noProgramLevel?: boolean;
  /** Why it is missing, from what the level IS priced at ("...has Group prices for 45 and 60 min..."). */
  hint?: string;
}

/**
 * Extract the missing-price list from an error thrown by an invoice-generation
 * call, or null if this isn't a missing-price error.
 */
export function getMissingPrices(error: unknown): MissingPrice[] | null {
  const details = (error as { response?: { data?: { details?: { missingPrices?: unknown } } } })
    ?.response?.data?.details;
  const list = details?.missingPrices;
  return Array.isArray(list) && list.length > 0 ? (list as MissingPrice[]) : null;
}

/** "MINI — Piano Hybrid · 45 min · Individual" */
export function formatMissingPrice(m: MissingPrice): string {
  if (m.noProgramLevel) return `${m.lessonName} · This class has no program level`;
  const cat = m.category === 'INDIVIDUAL' ? 'Individual' : m.category === 'GROUP' ? 'Group' : m.category;
  const level = m.programLevelName ? `${m.programLevelName} — ` : '';
  const zero = m.zeroPrice ? ' (set to $0.00)' : '';
  return `${level}${m.lessonName} · ${m.durationMinutes} min · ${cat}${zero}`;
}
